import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface WsMessage {
  type: string;
  payload?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Thin WebSocket client that:
 *  - Derives the WS URL from environment.apiHost (http→ws, https→wss)
 *  - Sends { type:'auth', token } immediately on open
 *  - Reconnects with exponential back-off (capped at 30 s) after any close
 *  - Exposes `on(eventType)` returning a typed Observable of the event payload
 */
@Injectable({ providedIn: 'root' })
export class WebSocketService implements OnDestroy {
  private socket: WebSocket | null = null;
  private readonly messages$ = new Subject<WsMessage>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 2_000;
  private destroyed = false;

  constructor(private authService: AuthService) {}

  /**
   * Open the WebSocket and authenticate.
   * Idempotent — safe to call multiple times; no-op if already connected or no token.
   */
  connect(): void {
    if (this.destroyed || this.socket) return;
    const token = this.authService.getAccessToken();
    if (!token) return;

    const wsBase = environment.apiHost
      .replace(/^https/, 'wss')
      .replace(/^http/, 'ws');
    const socket = new WebSocket(`${wsBase}/ws`);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.reconnectDelay = 2_000;
      socket.send(JSON.stringify({ type: 'auth', token }));
    });

    socket.addEventListener('message', ({ data }) => {
      try {
        const msg: WsMessage = JSON.parse(data as string);
        if (msg.type) this.messages$.next(msg);
      } catch { /* non-JSON — ignore silently */ }
    });

    socket.addEventListener('close', () => {
      this.socket = null;
      if (!this.destroyed) this.scheduleReconnect();
    });

    // error is always followed by close; cleanup happens in the close handler
    socket.addEventListener('error', () => { /* handled by close */ });
  }

  /**
   * Returns an Observable that emits the payload of every server event
   * whose `type` matches the given string.
   */
  on<T = Record<string, unknown>>(type: string): Observable<T> {
    return this.messages$.pipe(
      filter(m => m.type === type),
      map(m => (m.payload ?? {}) as T),
    );
  }

  disconnect(): void {
    this.destroyed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  private scheduleReconnect(): void {
    this.reconnectTimer = setTimeout(() => {
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000);
      this.connect();
    }, this.reconnectDelay);
  }
}
